// Main entry point following Single Responsibility Principle
import { ValidatorFactory } from './core/ValidatorFactory.js';
import { GeneratorFactory } from './core/GeneratorFactory.js';
import { IdDetector } from './core/IdDetector.js';

/**
 * Main API class following Facade pattern for simplicity (KISS)
 */
export class UniversalIdValidator {
  constructor() {
    this.validatorFactory = new ValidatorFactory();
    this.generatorFactory = new GeneratorFactory();
    this.detector = new IdDetector();
  }

  /**
   * Validate an ID with optional type specification
   */
  validate(id, type = null) {
    const idType = type || this.detector.detect(id);
    if (!idType) {
      return {
        isValid: false,
        message: 'Unable to detect ID type',
        details: {}
      };
    }
    const validator = this.validatorFactory.create(idType);
    return validator.validate(id);
  }

  /**
   * Generate a valid ID of specified type
   */
  generate(type, options = {}) {
    const generator = this.generatorFactory.create(type);
    return generator.generate(options);
  }

  /**
   * Auto-detect ID type
   */
  detectType(id) {
    return this.detector.detect(id);
  }

  /**
   * Get supported ID types
   */
  getSupportedTypes() {
    return this.validatorFactory.getSupportedTypes();
  }
}

// Export factory instances for direct use
export { ValidatorFactory, GeneratorFactory, IdDetector };

// Export core classes
export { BaseValidator } from './core/BaseValidator.js';
export { BaseGenerator } from './core/BaseGenerator.js';
export { ValidationResult } from './core/ValidationResult.js';

// Export all validators for direct access
export { HkidValidator } from './validators/HkidValidator.js';
export { TwidValidator } from './validators/TwidValidator.js';
export { AadhaarValidator } from './validators/AadhaarValidator.js';
export { NricValidator } from './validators/NricValidator.js';
export { FinValidator } from './validators/FinValidator.js';
export { PanValidator } from './validators/PanValidator.js';
export { SsnValidator } from './validators/SsnValidator.js';
export { SinValidator } from './validators/SinValidator.js';
export { NinoValidator } from './validators/NinoValidator.js';
export { BsnValidator } from './validators/BsnValidator.js';
export { CpfValidator } from './validators/CpfValidator.js';
export { RutValidator } from './validators/RutValidator.js';
export { CuilValidator } from './validators/CuilValidator.js';
export { RfcValidator } from './validators/RfcValidator.js';
export { PersonnummerValidator } from './validators/PersonnummerValidator.js';
export { PersonalnummerValidator } from './validators/PersonalnummerValidator.js';
export { CprValidator } from './validators/CprValidator.js';
export { PeselValidator } from './validators/PeselValidator.js';
export { CnpValidator } from './validators/CnpValidator.js';

// Export new comprehensive validators
export { AusValidator } from './validators/AusValidator.js';
export { NzValidator } from './validators/NzValidator.js';
export { JpValidator } from './validators/JpValidator.js';
export { KrValidator } from './validators/KrValidator.js';
export { CnValidator } from './validators/CnValidator.js';
export { ThValidator } from './validators/ThValidator.js';
export { MyValidator } from './validators/MyValidator.js';
export { PhValidator } from './validators/PhValidator.js';
export { VnValidator } from './validators/VnValidator.js';
export { IdValidator } from './validators/IdValidator.js';
export { ZaValidator } from './validators/ZaValidator.js';
export { EgValidator } from './validators/EgValidator.js';
export { NgValidator } from './validators/NgValidator.js';
export { KeValidator } from './validators/KeValidator.js';
export { GhValidator } from './validators/GhValidator.js';
export { FrValidator } from './validators/FrValidator.js';
export { DeValidator } from './validators/DeValidator.js';
export { ItValidator } from './validators/ItValidator.js';
export { EsValidator } from './validators/EsValidator.js';
export { PtValidator } from './validators/PtValidator.js';
export { RuValidator } from './validators/RuValidator.js';
export { UaValidator } from './validators/UaValidator.js';
export { TrValidator } from './validators/TrValidator.js';
export { IlValidator } from './validators/IlValidator.js';
export { ArValidator } from './validators/ArValidator.js';
export { SaValidator } from './validators/SaValidator.js';
export { PkValidator } from './validators/PkValidator.js';
export { BdValidator } from './validators/BdValidator.js';
export { LkValidator } from './validators/LkValidator.js';
export { MmValidator } from './validators/MmValidator.js';

// Export all generators for direct access
export { HkidGenerator } from './generators/HkidGenerator.js';
export { TwidGenerator } from './generators/TwidGenerator.js';
export { AadhaarGenerator } from './generators/AadhaarGenerator.js';
export { NricGenerator } from './generators/NricGenerator.js';
export { FinGenerator } from './generators/FinGenerator.js';
export { PanGenerator } from './generators/PanGenerator.js';
export { SsnGenerator } from './generators/SsnGenerator.js';
export { SinGenerator } from './generators/SinGenerator.js';
export { NinoGenerator } from './generators/NinoGenerator.js';
export { BsnGenerator } from './generators/BsnGenerator.js';
export { CpfGenerator } from './generators/CpfGenerator.js';
export { RutGenerator } from './generators/RutGenerator.js';
export { CuilGenerator } from './generators/CuilGenerator.js';
export { RfcGenerator } from './generators/RfcGenerator.js';
export { PersonnummerGenerator } from './generators/PersonnummerGenerator.js';
export { PersonalnummerGenerator } from './generators/PersonalnummerGenerator.js';
export { CprGenerator } from './generators/CprGenerator.js';
export { PeselGenerator } from './generators/PeselGenerator.js';
export { CnpGenerator } from './generators/CnpGenerator.js';

// Export new comprehensive generators
export { AusGenerator } from './generators/AusGenerator.js';
export { NzGenerator } from './generators/NzGenerator.js';
export { JpGenerator } from './generators/JpGenerator.js';
export { KrGenerator } from './generators/KrGenerator.js';
export { CnGenerator } from './generators/CnGenerator.js';
export { ThGenerator } from './generators/ThGenerator.js';
export { MyGenerator } from './generators/MyGenerator.js';
export { PhGenerator } from './generators/PhGenerator.js';
export { VnGenerator } from './generators/VnGenerator.js';
export { IdGenerator } from './generators/IdGenerator.js';
export { ZaGenerator } from './generators/ZaGenerator.js';
export { EgGenerator } from './generators/EgGenerator.js';
export { NgGenerator } from './generators/NgGenerator.js';
export { KeGenerator } from './generators/KeGenerator.js';
export { GhGenerator } from './generators/GhGenerator.js';
export { FrGenerator } from './generators/FrGenerator.js';
export { DeGenerator } from './generators/DeGenerator.js';
export { ItGenerator } from './generators/ItGenerator.js';
export { EsGenerator } from './generators/EsGenerator.js';
export { PtGenerator } from './generators/PtGenerator.js';
export { RuGenerator } from './generators/RuGenerator.js';
export { UaGenerator } from './generators/UaGenerator.js';
export { TrGenerator } from './generators/TrGenerator.js';
export { IlGenerator } from './generators/IlGenerator.js';
export { ArGenerator } from './generators/ArGenerator.js';
export { SaGenerator } from './generators/SaGenerator.js';
export { PkGenerator } from './generators/PkGenerator.js';
export { BdGenerator } from './generators/BdGenerator.js';
export { LkGenerator } from './generators/LkGenerator.js';
export { MmGenerator } from './generators/MmGenerator.js';

// Export utilities
export { ChecksumCalculator } from './utils/ChecksumCalculator.js';

// Default export for convenience
export default UniversalIdValidator;